import { CompanyAsyncSelect } from '@/components/features/Projects/components/CompanyAsyncSelect';
import { ContactAsyncSelect } from '@/components/features/Projects/components/ContactAsyncSelect';
import { LeadSelect, type LeadOption } from '@/components/global/LeadSelect';
import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/shadcn/form';
import { Input } from '@/components/ui/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/shadcn/select';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrencyInput, normalizeCurrencyInput } from '@/lib/utils';
import { lazy, Suspense } from 'react';

const AddressMapCard = lazy(() => import('@/components/global/address/AddressMapCard').then((m) => ({ default: m.AddressMapCard })));
const AddressSearchInput = lazy(() => import('@/components/global/address/AddressSearchInput').then((m) => ({ default: m.AddressSearchInput })));
const GoogleMapsProvider = lazy(() =>
    import('@/components/global/address/GoogleMapsProviderSingleton').then((m) => ({ default: m.GoogleMapsProvider })),
);

import type { FormDataConvertible } from '@inertiajs/core';
import { router, usePage } from '@inertiajs/react';
import { Ban, Euro, Flag, Globe, Info, LayoutTemplate } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { route } from 'ziggy-js';
import * as z from 'zod';

/** Defaults the form is opened with when a lead is being converted. */
export interface LeadDefaults {
    id: string;
    lead_number: string | null;
    /** The lead's object name — the project's contract name is the same object's. */
    object_name: string | null;
    company_id: string | null;
    company_name: string | null;
    contact_id: string | null;
    contact_first_name: string | null;
    contact_last_name: string | null;
    project_owner_id: string | null;
    country_type: 'local' | 'export';
    budget: string;
    offer_id: string | null;
    address_street: string | null;
    address_house_number: string | null;
    address_postal_code: string | null;
    address_city: string | null;
    address_municipality: string | null;
    address_country: string | null;
    address_lat: number | null;
    address_lng: number | null;
}

interface ProjectFormProps {
    /** Everyone who can own a project, commercially-responsible roles first. */
    ownerCandidates?: { people: PersonOption[]; suggested_ids: string[] };
    /**
     * Leads this form may be pointed at. Given only where picking one makes sense —
     * the create page — and omitted by the sheet, which is already fixed to a lead.
     */
    leadOptions?: LeadOption[];
    /** Present when converting a lead — the same form, prefilled from it. */
    initialLead?: LeadDefaults | null;
    /** Overrides the Cancel button, so a sheet can close instead of navigating back. */
    onCancel?: () => void;
}

export function ProjectForm({ ownerCandidates = { people: [], suggested_ids: [] }, leadOptions, initialLead, onCancel }: ProjectFormProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const noTemplateValue = '__none__';
    const page = usePage();
    const errors = useMemo(() => (page.props as { errors?: Record<string, string> }).errors || {}, [page.props]);
    const authUserId = (page.props as { auth?: { user?: { id?: string } } }).auth?.user?.id || null;

    const formSchema = useMemo(
        () =>
            z
                .object({
                    contact_id: z.string().optional(),
                    project_task_template: z.string().optional(),
                    contract_name: z
                        .string()
                        .min(1, t('projects.create.validation.contract_name_required', 'Contract name is required'))
                        .max(255, t('projects.create.validation.contract_name_max', 'Contract name cannot exceed 255 characters')),
                    company_id: z.string().min(1, t('projects.create.validation.company_required', 'Company is required')),
                    project_owner_id: z.string().min(1, t('projects.create.validation.owner_required', 'Project owner is required')),
                    country_type: z.enum(['local', 'export'], {
                        error: t('projects.create.validation.country_type_required', 'Country type is required'),
                    }),
                    signature_date: z.string().min(1, t('projects.create.validation.signature_date_required', 'Signature date is required')),
                    start_date: z.string().min(1, t('projects.create.validation.start_date_required', 'Start date is required')),
                    end_date: z.string().min(1, t('projects.create.validation.end_date_required', 'End date is required')),
                    budget: z
                        .string()
                        .min(1, t('projects.create.validation.budget_required', 'Budget is required'))
                        .refine((value) => !Number.isNaN(Number(normalizeCurrencyInput(value))) && Number(normalizeCurrencyInput(value)) >= 0, {
                            message: t('projects.create.validation.budget_invalid', 'Budget must be a valid number'),
                        }),
                    address: z.string().min(1, t('projects.create.validation.address_required', 'Address is required')),
                    address_lat: z.number().nullable().optional(),
                    address_lng: z.number().nullable().optional(),
                    address_street: z.string().nullable().optional(),
                    address_house_number: z.string().nullable().optional(),
                    address_postal_code: z.string().nullable().optional(),
                    address_city: z.string().nullable().optional(),
                    address_municipality: z.string().nullable().optional(),
                    address_country: z.string().nullable().optional(),
                })
                .superRefine((data, ctx) => {
                    if (data.signature_date && data.start_date && data.start_date < data.signature_date) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['start_date'],
                            message: t('projects.create.validation.start_before_signature', 'Start date cannot be before the signature date'),
                        });
                    }
                    if (data.start_date && data.end_date && data.end_date <= data.start_date) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['end_date'],
                            message: t('projects.create.validation.end_before_start', 'End date must be after the start date'),
                        });
                    }
                }),
        [t],
    );

    type FormValues = z.infer<typeof formSchema>;

    // Pre-select the "standard" task template when it exists so a sensible default is applied on create.
    // Computed synchronously from settings (already loaded via the app-root SettingsProvider on client-side
    // navigation) so the Select reflects it on first render; the effect below covers a cold hard-refresh.
    const defaultProjectTaskTemplate = useMemo(
        () =>
            (settings.projectTaskTemplates ?? []).some((template) => (template as { value?: unknown } | null)?.value === 'standard')
                ? 'standard'
                : '',
        [settings.projectTaskTemplates],
    );

    const leadAddressLabel = useMemo(
        () =>
            initialLead
                ? [initialLead.address_street, initialLead.address_house_number, initialLead.address_city, initialLead.address_postal_code]
                      .filter(Boolean)
                      .join(', ')
                : '',
        [initialLead],
    );

    const form = useForm<FormValues>({
        mode: 'onTouched',
        defaultValues: {
            contact_id: initialLead?.contact_id ?? '',
            project_task_template: defaultProjectTaskTemplate,
            // Converting a lead carries its object name over — one object, one name.
            contract_name: initialLead?.object_name ?? '',
            company_id: initialLead?.company_id ?? '',
            project_owner_id: initialLead?.project_owner_id ?? authUserId ?? '',
            country_type: initialLead?.country_type ?? 'local',
            signature_date: '',
            start_date: '',
            end_date: '',
            budget: initialLead ? formatCurrencyInput(initialLead.budget) : '',
            address: leadAddressLabel,
            address_lat: initialLead?.address_lat ?? null,
            address_lng: initialLead?.address_lng ?? null,
            address_street: initialLead?.address_street ?? null,
            address_house_number: initialLead?.address_house_number ?? null,
            address_postal_code: initialLead?.address_postal_code ?? null,
            address_city: initialLead?.address_city ?? null,
            address_municipality: initialLead?.address_municipality ?? null,
            address_country: initialLead?.address_country ?? null,
        },
    });
    const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof FormValues, string>>>({});

    const clearFieldError = useCallback(
        (...fields: Array<keyof FormValues>) => {
            form.clearErrors(fields);
            setValidationErrors((previous) => {
                const next = { ...previous };

                fields.forEach((field) => {
                    delete next[field];
                });

                return next;
            });
        },
        [form],
    );

    const fieldError = useCallback(
        (field: keyof FormValues): string | undefined => {
            return validationErrors[field] ?? form.getFieldState(field).error?.message?.toString();
        },
        [form, validationErrors],
    );

    // Changing the company invalidates the contact — but not on first render, which
    // would throw away the contact the lead came with.
    const watchedCompanyId = form.watch('company_id');
    const [initialCompanyId] = useState(() => form.getValues('company_id'));
    useEffect(() => {
        if (watchedCompanyId !== initialCompanyId && form.getValues('contact_id')) {
            form.setValue('contact_id', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedCompanyId]);

    useEffect(() => {
        setValidationErrors((previous) => ({ ...previous, ...(errors as Partial<Record<keyof FormValues, string>>) }));

        Object.entries(errors).forEach(([field, message]) => {
            form.setError(field as keyof FormValues, { type: 'server', message });
        });
    }, [errors, form]);

    const watchedAddress = form.watch('address');
    const watchedAddressLat = form.watch('address_lat');
    const watchedAddressLng = form.watch('address_lng');

    const projectCountryTypes = (
        settings.projectCountryTypes?.length
            ? settings.projectCountryTypes
            : [
                  { value: 'local', label: t('projects.create.country_types.local', 'Local'), color: '' },
                  { value: 'export', label: t('projects.create.country_types.export', 'Export'), color: '' },
              ]
    ).map((option) => {
        if (option.value === 'local') {
            return { ...option, label: t('projects.create.country_types.local', option.label ?? 'Local') };
        }

        if (option.value === 'export') {
            return { ...option, label: t('projects.create.country_types.export', option.label ?? 'Export') };
        }

        return option;
    });

    const projectTaskTemplateOptions = useMemo(() => {
        const templates = settings.projectTaskTemplates ?? [];
        return templates
            .map((template) => {
                if (!template || typeof template !== 'object') {
                    return null;
                }
                const record = template as Record<string, unknown>;
                const value =
                    typeof record.value === 'string'
                        ? record.value
                        : typeof record.key === 'string'
                          ? record.key
                          : typeof record.name === 'string'
                            ? record.name
                            : '';
                const label = typeof record.label === 'string' ? record.label : typeof record.name === 'string' ? record.name : value;
                if (!value) {
                    return null;
                }
                return { value, label };
            })
            .filter((template): template is { value: string; label: string } => Boolean(template));
    }, [settings.projectTaskTemplates]);

    useEffect(() => {
        const hasStandardTemplate = projectTaskTemplateOptions.some((option) => option.value === 'standard');
        if (hasStandardTemplate && !form.getValues('project_task_template')) {
            form.setValue('project_task_template', 'standard');
        }
    }, [projectTaskTemplateOptions, form]);

    /** Country of an address picked while the region still says local — drives the confirm below. */
    const [regionMismatch, setRegionMismatch] = useState<string | null>(null);

    /**
     * The setup wizard stores the home country as an ISO-2 code, but the shipped default
     * is a name — so compare against whichever of the two the address supplies. An address
     * that carries neither, or no home country configured, is left alone rather than queried.
     */
    const isOutsideHomeCountry = useCallback(
        (address: { country?: string; country_code?: string }): boolean => {
            const home = String(settings.homeCountry ?? '').trim();

            if (home === '') {
                return false;
            }

            if (home.length <= 3) {
                const code = (address.country_code ?? '').trim();

                return code !== '' && code.toUpperCase() !== home.toUpperCase();
            }

            const name = (address.country ?? '').trim();

            return name !== '' && name.toLowerCase() !== home.toLowerCase();
        },
        [settings.homeCountry],
    );

    // Re-requests the page for the chosen lead: the server already knows how to fill
    // this form from one, so selecting is a navigation rather than a second prefill path.
    const selectLead = (leadId: string | null) => {
        router.get(route('projects.create'), leadId ? { lead_id: leadId } : {}, { preserveScroll: true });
    };

    const requiredMark = <span className="text-destructive">*</span>;
    const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        form.clearErrors();
        setValidationErrors({});

        const validation = formSchema.safeParse(form.getValues());

        if (!validation.success) {
            const invalidFields = new Set<keyof FormValues>();
            const nextErrors: Partial<Record<keyof FormValues, string>> = {};

            validation.error.issues.forEach((issue) => {
                const fieldName = issue.path[0];

                if (typeof fieldName !== 'string' || invalidFields.has(fieldName as keyof FormValues)) {
                    return;
                }

                invalidFields.add(fieldName as keyof FormValues);
                nextErrors[fieldName as keyof FormValues] = issue.message;
                form.setError(fieldName as keyof FormValues, { type: 'manual', message: issue.message });
            });
            setValidationErrors(nextErrors);

            toast.error(t('projects.create.validation.resolve_errors', 'Please fill in all required fields.'));

            return;
        }

        const values = validation.data;
        const payload: Record<string, FormDataConvertible> = {
            ...values,
            budget: Number(normalizeCurrencyInput(values.budget)),
        };

        if (!values.project_task_template) {
            delete payload.project_task_template;
        }
        if (!values.contact_id) {
            delete payload.contact_id;
        }

        if (initialLead) {
            // The lead owns the company and the country type, and the endpoint derives
            // both server-side, so they are not worth posting back.
            delete payload.company_id;
            payload.offer_id = initialLead.offer_id ?? '';
            router.post(route('projects.store-from-lead', initialLead.id), payload);

            return;
        }

        router.post(route('projects.store'), payload);
    };

    return (
        <Form {...form}>
            <form className="space-y-6 pb-6" onSubmit={handleSubmit}>
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                1
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('projects.create.sections.details_and_ownership', 'Details & Ownership')}</CardTitle>
                                <CardDescription>{t('projects.create.required_hint', 'Fields marked with * are mandatory.')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {leadOptions ? (
                            /* Optional, but it fills the whole form and closes the lead on
                               save — set apart so it does not read as one field among many. */
                            <FormItem className="bg-muted/40 space-y-2 rounded-lg border border-dashed p-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <FormLabel className="text-sm font-semibold">{t('projects.create.fields.lead', 'Lead')}</FormLabel>
                                    <Badge variant="secondary" className="text-xs font-normal">
                                        {t('common.optional', 'Optional')}
                                    </Badge>
                                </div>
                                <FormControl>
                                    <LeadSelect leads={leadOptions} value={initialLead?.id ?? null} onValueChange={selectLead} />
                                </FormControl>
                                <p className="text-muted-foreground text-xs">
                                    {t('projects.create.lead_hint', 'Pick a lead to fill this form from it and mark it won on save.')}
                                </p>
                            </FormItem>
                        ) : null}

                        <FormField
                            control={form.control}
                            name="contract_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.contract_name', 'Contract Name')} {requiredMark}
                                    </FormLabel>
                                    <FormControl className="w-full">
                                        <Input
                                            className={cn('w-full', fieldError('contract_name') && invalidFieldClassName)}
                                            {...field}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                clearFieldError('contract_name');
                                            }}
                                        />
                                    </FormControl>
                                    {initialLead ? (
                                        <p className="text-muted-foreground text-xs">
                                            {t(
                                                'projects.create.hints.contract_name_from_lead',
                                                "Taken from the lead's object name. Renaming the lead renames this too.",
                                            )}
                                        </p>
                                    ) : null}
                                    <FormMessage>{fieldError('contract_name')}</FormMessage>
                                </FormItem>
                            )}
                        />

                        <div className="grid items-start gap-4 md:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="company_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.create.fields.company', 'Company')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <CompanyAsyncSelect
                                                seedCompanies={
                                                    initialLead?.company_id
                                                        ? [{ id: initialLead.company_id, name: initialLead.company_name ?? '' }]
                                                        : undefined
                                                }
                                                value={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    clearFieldError('company_id');
                                                }}
                                                disabled={Boolean(initialLead)}
                                                placeholder={t('projects.create.placeholders.company', 'Select a company')}
                                                className={cn(fieldError('company_id') && invalidFieldClassName)}
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldError('company_id')}</FormMessage>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="project_owner_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.create.fields.owner', 'Project Owner')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <PersonSelect
                                                people={ownerCandidates.people}
                                                suggestedIds={ownerCandidates.suggested_ids}
                                                showRole
                                                variant="field"
                                                value={field.value || null}
                                                onValueChange={(value) => {
                                                    field.onChange(value ?? '');
                                                    clearFieldError('project_owner_id');
                                                }}
                                                labels={{ placeholder: t('projects.create.placeholders.owner', 'Select an owner') }}
                                                className={cn(fieldError('project_owner_id') && invalidFieldClassName)}
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldError('project_owner_id')}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="contact_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('projects.create.fields.contact', 'Contact Person (optional)')}</FormLabel>
                                    <FormControl>
                                        <ContactAsyncSelect
                                            companyId={watchedCompanyId || undefined}
                                            seedPeople={
                                                initialLead?.contact_id
                                                    ? [
                                                          {
                                                              uuid: initialLead.contact_id,
                                                              first_name: initialLead.contact_first_name ?? '',
                                                              last_name: initialLead.contact_last_name ?? '',
                                                          },
                                                      ]
                                                    : undefined
                                            }
                                            value={field.value || undefined}
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                                clearFieldError('contact_id');
                                            }}
                                            placeholder={t('projects.create.placeholders.contact', 'Select a contact...')}
                                            companyName={initialLead?.company_name ?? null}
                                            disabled={!watchedCompanyId}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </CardContent>
                </Card>

                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                2
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('projects.create.sections.timeline_and_budget', 'Timeline & Budget')}</CardTitle>
                                <CardDescription>
                                    {t('projects.create.sections.timeline_and_budget_desc', 'Set project dates and value.')}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid items-start gap-4 md:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="signature_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.create.fields.signature_date', 'Signature Date')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                value={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    clearFieldError('signature_date', 'start_date', 'end_date');
                                                }}
                                                placeholder={t('projects.create.placeholders.pick_date', 'Pick a date')}
                                                className={cn(fieldError('signature_date') && invalidFieldClassName)}
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldError('signature_date')}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid items-start gap-4 md:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="start_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.create.fields.start_date', 'Start Date')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                value={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    clearFieldError('signature_date', 'start_date', 'end_date');
                                                }}
                                                placeholder={t('projects.create.placeholders.pick_date', 'Pick a date')}
                                                className={cn(fieldError('start_date') && invalidFieldClassName)}
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldError('start_date')}</FormMessage>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="end_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.create.fields.end_date', 'End Date')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                value={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    clearFieldError('start_date', 'end_date');
                                                }}
                                                placeholder={t('projects.create.placeholders.pick_date', 'Pick a date')}
                                                className={cn(fieldError('end_date') && invalidFieldClassName)}
                                            />
                                        </FormControl>
                                        <FormMessage>{fieldError('end_date')}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="budget"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.budget', 'Budget')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Euro className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                {...field}
                                                type="text"
                                                inputMode="decimal"
                                                className={cn('pl-9', fieldError('budget') && invalidFieldClassName)}
                                                onChange={(event) => {
                                                    field.onChange(formatCurrencyInput(event.target.value));
                                                    clearFieldError('budget');
                                                }}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage>{fieldError('budget')}</FormMessage>
                                </FormItem>
                            )}
                        />
                        <div className="border-primary/25 bg-primary/5 flex items-start gap-2 rounded-sm border p-3 text-sm">
                            <Info className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                            <p className="text-foreground">{t('projects.create.budget_hint', 'Enter total project budget excluding VAT.')}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                3
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('projects.create.sections.address', 'Address')}</CardTitle>
                                <CardDescription>
                                    {t('projects.create.sections.address_desc', 'Project location or primary object address.')}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Region leads the address: it is a property of where the work is,
                                and picking an address out of country offers to correct it. */}
                        <FormField
                            control={form.control}
                            name="country_type"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.region', 'Region')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            {projectCountryTypes.map((option) => {
                                                const isActive = field.value === option.value;
                                                const icon = option.value === 'local' ? <Flag className="h-4 w-4" /> : <Globe className="h-4 w-4" />;

                                                return (
                                                    <Button
                                                        key={option.value}
                                                        type="button"
                                                        variant="outline"
                                                        disabled={Boolean(initialLead)}
                                                        className={cn(
                                                            'h-11 justify-center gap-2 rounded-sm',
                                                            isActive
                                                                ? 'border-primary bg-primary/15 text-primary ring-primary/50 hover:bg-primary/20 dark:bg-primary/20 ring-1'
                                                                : 'hover:bg-muted/60',
                                                        )}
                                                        onClick={() => {
                                                            field.onChange(option.value);
                                                            clearFieldError('country_type');
                                                        }}
                                                    >
                                                        {icon}
                                                        {option.label}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="address"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.address', 'Address')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <div
                                            className={cn(
                                                'space-y-4',
                                                fieldError('address') &&
                                                    '[&_input]:border-destructive [&_input]:ring-destructive/20 [&_input]:ring-[3px]',
                                            )}
                                        >
                                            <Suspense fallback={<div className="text-muted-foreground p-4 text-sm">Loading…</div>}>
                                                <GoogleMapsProvider>
                                                    <AddressSearchInput
                                                        value={field.value ?? ''}
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            clearFieldError('address');
                                                            form.setValue('address_lat', null);
                                                            form.setValue('address_lng', null);
                                                            form.setValue('address_street', null);
                                                            form.setValue('address_house_number', null);
                                                            form.setValue('address_postal_code', null);
                                                            form.setValue('address_city', null);
                                                            form.setValue('address_municipality', null);
                                                            form.setValue('address_country', null);
                                                        }}
                                                        onAddressSelect={(address) => {
                                                            field.onChange(address.label);
                                                            clearFieldError('address');
                                                            form.setValue('address_lat', address.lat ?? null);
                                                            form.setValue('address_lng', address.lng ?? null);

                                                            if (address.street) form.setValue('address_street', address.street);
                                                            if (address.house_number) form.setValue('address_house_number', address.house_number);
                                                            if (address.city) form.setValue('address_city', address.city);
                                                            if (address.postal_code) form.setValue('address_postal_code', address.postal_code);
                                                            if (address.country) form.setValue('address_country', address.country);

                                                            // A lead owns its own region and the server recomputes it on
                                                            // conversion, so only ask on a form the user is filling itself.
                                                            if (
                                                                !initialLead &&
                                                                form.getValues('country_type') === 'local' &&
                                                                isOutsideHomeCountry(address)
                                                            ) {
                                                                setRegionMismatch(address.country ?? address.country_code ?? '');
                                                            }
                                                        }}
                                                        placeholder={t('projects.create.placeholders.address', 'Enter or search for address...')}
                                                    />

                                                    {watchedAddressLat != null && watchedAddressLng != null && (
                                                        <AddressMapCard
                                                            address={watchedAddress || ''}
                                                            lat={watchedAddressLat}
                                                            lng={watchedAddressLng}
                                                        />
                                                    )}
                                                </GoogleMapsProvider>
                                            </Suspense>
                                        </div>
                                    </FormControl>
                                    <FormMessage>{fieldError('address')}</FormMessage>
                                </FormItem>
                            )}
                        />
                    </CardContent>
                </Card>

                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                4
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('projects.create.sections.template.name', 'Template')}</CardTitle>
                                <CardDescription>
                                    {t(
                                        'projects.create.sections.template.description',
                                        'This template will be applied to all tasks created for this project.',
                                    )}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <FormField
                            control={form.control}
                            name="project_task_template"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('projects.create.fields.templates', 'Select template')}</FormLabel>
                                    <Select
                                        value={field.value || noTemplateValue}
                                        onValueChange={(value) => {
                                            field.onChange(value === noTemplateValue ? '' : value);
                                            clearFieldError('project_task_template');
                                        }}
                                        disabled={projectTaskTemplateOptions.length === 0}
                                    >
                                        <FormControl>
                                            {/* Sized off the 24px badge, like PersonSelect and CompanySelector,
                                                so the three read as one family of controls. */}
                                            <SelectTrigger className="h-auto w-full justify-start gap-2 py-1.5 pl-1.5">
                                                <span
                                                    className={cn(
                                                        'text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                                                        field.value ? 'bg-muted' : 'border-muted-foreground/40 border border-dashed',
                                                    )}
                                                >
                                                    <LayoutTemplate className="h-3.5 w-3.5" />
                                                </span>
                                                <span className={cn('truncate text-sm', !field.value && 'text-muted-foreground')}>
                                                    {projectTaskTemplateOptions.find((option) => option.value === field.value)?.label ??
                                                        t('projects.create.clear_template', 'No template')}
                                                </span>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value={noTemplateValue} textValue={t('projects.create.clear_template', 'No template')}>
                                                <span className="flex items-center gap-2">
                                                    <Ban className="text-muted-foreground h-4 w-4 shrink-0" />
                                                    {t('projects.create.clear_template', 'No template')}
                                                </span>
                                            </SelectItem>
                                            {projectTaskTemplateOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value} textValue={option.label}>
                                                    <span className="flex items-center gap-2">
                                                        <LayoutTemplate className="text-muted-foreground h-4 w-4 shrink-0" />
                                                        {option.label}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="border-primary/20 bg-primary/5 text-primary-foreground rounded-sm border p-3">
                            <p className="text-foreground text-sm">
                                {t('projects.create.template_hint', 'Template helps you start faster by pre-filling default project tasks.')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <div className="bg-background/95 sticky bottom-0 z-10 flex justify-end gap-2 rounded-md border p-4 backdrop-blur">
                    <Button type="button" variant="outline" onClick={() => (onCancel ? onCancel() : window.history.back())}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="submit">{t('projects.create.actions.create', 'Create Project')}</Button>
                </div>

                {/* Answering "no" keeps the region as chosen — the address is not
                        re-checked, so a deliberate local classification stays put. */}
                <AlertDialog open={regionMismatch !== null} onOpenChange={(open) => (open ? null : setRegionMismatch(null))}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('projects.create.region_mismatch.title', 'Change region to export?')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t(
                                    'projects.create.region_mismatch.description',
                                    'This address is in :country, outside your home country, but the region is set to local.',
                                ).replace(':country', regionMismatch ?? '')}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.no', 'No')}</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => {
                                    form.setValue('country_type', 'export');
                                    clearFieldError('country_type');
                                    setRegionMismatch(null);
                                }}
                            >
                                {t('common.yes', 'Yes')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </form>
        </Form>
    );
}
